import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { Background, Controls, Handle, Position, ReactFlow, ReactFlowProvider, addEdge, applyEdgeChanges, applyNodeChanges, useReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import api, { fmtErr, isDriveEmployee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LayoutGrid, FileDown, FileImage, Save, Search, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DND_TYPE = "application/pruva-person";
const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function PersonNode({ data, selected }) {
  const isLabel = data.kind === "label";
  return (
    <div
      className={`rounded-lg border-2 bg-white px-3 py-2 shadow-sm ${selected ? "border-sky-500" : isLabel ? "border-slate-800" : "border-slate-200"}`}
      style={{ width: 230 }}
      data-testid={`oc-node-${data.refId || data.name}`}
    >
      <Handle type="target" position={Position.Top} className="!bg-slate-400" />
      <div className="flex items-center gap-2.5">
        {isLabel ? (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-800 font-mono text-[11px] font-bold text-white">{initials(data.name)}</span>
        ) : data.kind === "manpower" && data.hasPhoto ? (
          <img src={`${API}/studio/manpower/${data.refId}/photo`} alt="" className="h-9 w-9 shrink-0 rounded-full border object-cover" />
        ) : (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-700 font-mono text-[11px] font-semibold text-white">{initials(data.name)}</span>
        )}
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold leading-tight text-slate-900">{data.name}</div>
          {data.title && <div className="truncate text-[11px] font-medium leading-tight text-sky-700">{data.title}</div>}
          {data.company && <div className="truncate text-[10px] leading-tight text-slate-400">{data.company}</div>}
          {data.stale && <div className="text-[9px] font-semibold uppercase text-rose-500">⚠ {data.staleLabel || "deleted"}</div>}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-slate-400" />
    </div>
  );
}

const nodeTypes = { person: PersonNode };

const dagreLayout = (nodes, edges) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", nodesep: 36, ranksep: 70 });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((n) => g.setNode(n.id, { width: 230, height: 96 }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map((n) => {
    const p = g.node(n.id);
    return { ...n, position: { x: p.x - 115, y: p.y - 48 } };
  });
};

function OrgCanvas() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedId, project } = useProject();
  const qc = useQueryClient();
  const { screenToFlowPosition } = useReactFlow();
  const wrapRef = useRef(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [poolSearch, setPoolSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  const { data: membersData } = useQuery({
    queryKey: ["project-members", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/members`)).data,
    enabled: !!selectedId,
  });
  const { data: manpower = [] } = useQuery({
    queryKey: ["studio-manpower"],
    queryFn: async () => (await api.get("/studio/manpower")).data,
  });
  const { data: chart, isLoading } = useQuery({
    queryKey: ["studio-orgchart", selectedId],
    queryFn: async () => (await api.get(`/studio/orgchart/${selectedId}`)).data,
    enabled: !!selectedId,
  });

  useEffect(() => {
    if (!chart) return;
    setNodes((chart.nodes || []).map((n) => ({
      id: n.id,
      type: "person",
      position: n.position,
      data: { kind: n.kind, refId: n.ref_id, name: n.name, title: n.title, company: n.company, hasPhoto: n.has_photo, stale: n.stale, staleLabel: t("studio.staleRef") },
    })));
    setEdges((chart.edges || []).map((e) => ({ id: e.id, source: e.source, target: e.target, type: "smoothstep" })));
  }, [chart, t]);

  const saveMut = useMutation({
    mutationFn: async () =>
      api.put(`/studio/orgchart/${selectedId}`, {
        nodes: nodes.map((n) => ({ id: n.id, kind: n.data.kind, ref_id: n.data.refId, name: n.data.name, title: n.data.title, company: n.data.company, has_photo: n.data.hasPhoto, position: n.position })),
        edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target })),
      }),
    onSuccess: () => { toast.success(t("studio.savedToast")); qc.invalidateQueries({ queryKey: ["studio-orgchart", selectedId] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const onNodesChange = useCallback((c) => setNodes((ns) => applyNodeChanges(c, ns)), []);
  const onEdgesChange = useCallback((c) => setEdges((es) => applyEdgeChanges(c, es)), []);
  const onConnect = useCallback((c) => {
    if (c.source === c.target) return;
    setEdges((es) => addEdge({ ...c, type: "smoothstep" }, es));
  }, []);

  const pool = useMemo(() => {
    const parties = membersData?.parties || [];
    const members = (membersData?.members || []).map((m) => {
      const party = parties.find((p) => p.id === m.party_id);
      return { kind: "user", refId: m.id, name: m.name, title: m.job_title || "", company: party?.company_name || "" };
    });
    const mp = manpower.map((m) => ({ kind: "manpower", refId: m.id, name: m.name, title: m.title, company: m.company, hasPhoto: m.has_photo }));
    const q = poolSearch.trim().toLowerCase();
    const match = (p) => !q || [p.name, p.title, p.company].some((v) => (v || "").toLowerCase().includes(q));
    return { members: members.filter(match), manpower: mp.filter(match) };
  }, [membersData, manpower, poolSearch]);

  const onChart = useCallback((p) => nodes.some((n) => n.data.kind === p.kind && n.data.refId === p.refId), [nodes]);

  const addPerson = useCallback((p, position) => {
    if (onChart(p)) return;
    setNodes((ns) => [...ns, { id: `n-${p.kind}-${p.refId}-${Date.now()}`, type: "person", position, data: { ...p, staleLabel: t("studio.staleRef") } }]);
  }, [onChart, t]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    try {
      const p = JSON.parse(e.dataTransfer.getData(DND_TYPE));
      addPerson(p, screenToFlowPosition({ x: e.clientX, y: e.clientY }));
    } catch { /* ignore */ }
  }, [addPerson, screenToFlowPosition]);

  const autoLayout = () => setNodes((ns) => dagreLayout(ns, edges));

  const autoDraft = () => {
    const parties = membersData?.parties || [];
    if (!parties.length) return;
    const nn = [];
    const ne = [];
    parties.forEach((p) => {
      const lid = `label-${p.id}`;
      nn.push({ id: lid, type: "person", position: { x: 0, y: 0 }, data: { kind: "label", refId: p.id, name: p.company_name, title: p.role, company: "" } });
      (membersData?.members || []).filter((m) => m.party_id === p.id).forEach((m) => {
        const nid = `u-${m.id}-${p.id}`;
        nn.push({ id: nid, type: "person", position: { x: 0, y: 0 }, data: { kind: "user", refId: m.id, name: m.name, title: m.job_title || "", company: p.company_name } });
        ne.push({ id: `e-${lid}-${nid}`, source: lid, target: nid, type: "smoothstep" });
      });
    });
    setNodes(dagreLayout(nn, ne));
    setEdges(ne);
  };

  const capture = async () => {
    const el = wrapRef.current?.querySelector(".react-flow__viewport");
    if (!el) throw new Error("no viewport");
    return toPng(el, { backgroundColor: "#ffffff", pixelRatio: 2 });
  };

  const exportPng = async () => {
    setExporting(true);
    try {
      const url = await capture();
      const a = document.createElement("a");
      a.href = url;
      a.download = `orgchart-${(project?.name || "project").replace(/\s+/g, "-")}.png`;
      a.click();
      toast.success(t("studio.exportDone"));
    } catch (e) { toast.error(fmtErr(e)); } finally { setExporting(false); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const url = await capture();
      const im = new Image();
      await new Promise((res, rej) => { im.onload = res; im.onerror = rej; im.src = url; });
      let logo = null;
      try {
        logo = new Image();
        await new Promise((res, rej) => { logo.onload = res; logo.onerror = rej; logo.src = "/pruva-logo-full.png"; });
      } catch { logo = null; }

      // Branded cover band (logo + project + date + TENDER SUBMISSION) drawn on EVERY page
      const bandPx = 130;
      const drawBand = (ctx, width) => {
        ctx.fillStyle = "#0f172a";
        ctx.fillRect(0, 0, width, bandPx);
        const textX = logo ? 150 : 24;
        if (logo) {
          const lh = 84;
          const lw = (logo.width / logo.height) * lh;
          ctx.drawImage(logo, 24, (bandPx - lh) / 2, lw, lh);
        }
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 44px sans-serif";
        ctx.textBaseline = "middle";
        ctx.fillText(project?.name || "", textX, 52);
        ctx.font = "28px sans-serif";
        ctx.fillStyle = "#94a3b8";
        ctx.fillText(new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }), textX, 100);
        ctx.font = "bold 30px sans-serif";
        ctx.fillStyle = "#38bdf8";
        ctx.textAlign = "right";
        ctx.fillText("TENDER SUBMISSION", width - 24, 66);
        ctx.textAlign = "left";
      };

      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const margin = 10;
      const w = 297 - 2 * margin;
      const maxH = 210 - 2 * margin;
      const scale = w / im.width;
      const sliceH = Math.max(50, Math.floor(maxH / scale - bandPx));
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      let y = 0;
      let first = true;
      while (y < im.height) {
        const hSrc = Math.min(sliceH, im.height - y);
        canvas.width = im.width;
        canvas.height = bandPx + hSrc;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, im.width, canvas.height);
        drawBand(ctx, im.width);
        ctx.drawImage(im, 0, y, im.width, hSrc, 0, bandPx, im.width, hSrc);
        if (!first) pdf.addPage();
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin, margin, w, (bandPx + hSrc) * scale);
        y += hSrc;
        first = false;
      }
      pdf.save(`orgchart-${(project?.name || "project").replace(/\s+/g, "-")}.pdf`);
      toast.success(t("studio.exportDone"));
    } catch (e) { toast.error(fmtErr(e)); } finally { setExporting(false); }
  };

  const PoolCard = ({ p }) => {
    const added = onChart(p);
    return (
      <div
        draggable={!added}
        onDragStart={(e) => e.dataTransfer.setData(DND_TYPE, JSON.stringify(p))}
        onClick={() => addPerson(p, { x: 80 + Math.random() * 200, y: 80 + Math.random() * 120 })}
        className={`mb-1.5 flex cursor-grab items-center gap-2 rounded-md border bg-white px-2 py-1.5 text-left transition-colors ${added ? "opacity-40" : "hover:border-sky-300 hover:bg-sky-50"}`}
        data-testid={`pool-${p.kind}-${p.refId}`}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-700 font-mono text-[9px] font-semibold text-white">{initials(p.name)}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-semibold text-slate-800">{p.name}</span>
          <span className="block truncate text-[10px] text-slate-400">{[p.title, p.company].filter(Boolean).join(" · ")}</span>
        </span>
        {added && <Badge variant="outline" className="text-[9px]">✓</Badge>}
      </div>
    );
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-4" data-testid="studio-orgchart-page">
      <div className="flex w-64 shrink-0 flex-col rounded-lg border bg-slate-50 p-3">
        <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-800">{t("studio.poolT")}</div>
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input value={poolSearch} onChange={(e) => setPoolSearch(e.target.value)} placeholder={t("studio.poolSearchPh")} className="h-8 bg-white pl-8 text-xs" data-testid="oc-pool-search" />
        </div>
        <p className="mb-2 text-[10px] text-slate-400">{t("studio.dragHint")}</p>
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t("studio.poolMembers")}</div>
          {pool.members.map((p) => <PoolCard key={`u-${p.refId}`} p={p} />)}
          <div className="mb-1 mt-3 font-mono text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t("studio.poolManpower")}</div>
          {pool.manpower.map((p) => <PoolCard key={`m-${p.refId}`} p={p} />)}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-display text-xl font-bold text-slate-900">{t("studio.orgChartTitle")}</h1>
          <span className="text-xs text-slate-400">{project?.name}</span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={autoDraft} data-testid="oc-autodraft"><Sparkles className="mr-1 h-4 w-4" /> {t("studio.autoDraft")}</Button>
            <Button size="sm" variant="outline" onClick={autoLayout} data-testid="oc-autolayout"><LayoutGrid className="mr-1 h-4 w-4" /> {t("studio.autoLayout")}</Button>
            <Button size="sm" variant="outline" onClick={exportPng} disabled={exporting || !nodes.length} data-testid="oc-export-png"><FileImage className="mr-1 h-4 w-4" /> {t("studio.exportPng")}</Button>
            <Button size="sm" variant="outline" onClick={exportPdf} disabled={exporting || !nodes.length} data-testid="oc-export-pdf"><FileDown className="mr-1 h-4 w-4" /> {t("studio.exportPdf")}</Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="oc-save"><Save className="mr-1 h-4 w-4" /> {t("studio.saveChart")}</Button>
          </div>
        </div>
        <div ref={wrapRef} className="min-h-0 flex-1 rounded-lg border bg-white" onDrop={onDrop} onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }} data-testid="oc-canvas">
          {!isLoading && nodes.length === 0 && (
            <div className="pointer-events-none absolute z-10 flex h-full w-full items-center justify-center">
              <p className="max-w-sm rounded-lg border border-dashed bg-white/90 px-4 py-3 text-center text-sm text-slate-400" data-testid="oc-empty">{t("studio.emptyChart")}</p>
            </div>
          )}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            deleteKeyCode={["Backspace", "Delete"]}
            fitView
            minZoom={0.2}
            proOptions={{ hideAttribution: false }}
          >
            <Background gap={20} color="#e2e8f0" />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
      </div>
    </div>
  );
}

export default function StudioOrgChart() {
  const { user } = useAuth();
  if (!isDriveEmployee(user)) return <Navigate to="/" replace />;
  return (
    <ReactFlowProvider>
      <OrgCanvas />
    </ReactFlowProvider>
  );
}
